import cuid from "cuid";
import {
  defaultTo,
  defaultsDeep,
  filter,
  get,
  groupBy,
  head,
  isEmpty,
  isNil,
  isNumber,
  isObject,
  keyBy,
  mapValues,
  set,
} from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import {
  createFieldStore,
  FieldRegistryProvider,
} from "shared/hooks/useFieldRegistry";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";

import moment from "moment";
import { USER_AUTH } from "utils/api/graphql/queries/users";
import {
  ATTACHMENT_TYPES,
  BANK_ACCOUNT_OWNER_TYPE,
  CASE_TYPES,
  CONTACT_POINT_SYSTEM,
  FAMILY_SITUATION,
  PAYMENT_RECEIVER,
  PROFILE_LIST,
  PROJECT_SOURCE,
  PROJECT_TYPES,
  RELATIONSHIP,
  SALE_MODES,
  SUBSCRIPTION_FORM_KEYS,
  SUBSCRIPTION_STATUSES,
} from "utils/constants";
import View from "./View";

export const STEPPER_KEYS = {
  ADDITIONAL_INFORMATION: "ADDITIONAL_INFORMATION",
  PROOF: "PROOF",
  DOCUMENT: "DOCUMENT",
};

const ADHERENT_DOCUMENTS = [
  ATTACHMENT_TYPES.CIN,
  ATTACHMENT_TYPES.SIGNED_DIRECT_MANDATE,
  ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
  ATTACHMENT_TYPES.PROFESSIONAL_PAYMENT_ACCOUNT,
  ATTACHMENT_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE,
  ATTACHMENT_TYPES.SOCIAL_SECURITY_CERTIFICATE,
  ATTACHMENT_TYPES.PAY_SLIP,
  ATTACHMENT_TYPES.ELECTRONIC_SIGNATURE_ELIGIBILITY,
];

const PAYER_DOCUMENTS = [
  ATTACHMENT_TYPES.KBIS,
  ATTACHMENT_TYPES.PAYER_CIN,
  ATTACHMENT_TYPES.PAYER_RIB,
];

const SPOUSE_DOCUMENTS = [
  ATTACHMENT_TYPES.SOCIAL_SECURITY_CERTIFICATE,
  ATTACHMENT_TYPES.MARITAL_CERTIFICATE,
];
const CHILDREN_DOCUMENTS = [
  ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF,
  ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
  ATTACHMENT_TYPES.SOCIAL_SECURITY_CERTIFICATE,
  ATTACHMENT_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE,
];

const Detail = () => {
  const { id } = useParams();
  const fieldStore = useMemo(() => createFieldStore(), []);
  const [initialValues, setInitialValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkUser] = useLazyQuery(USER_AUTH);
  const [updateSubscription] = useMutation(
    graphql.mutations.UPDATE_SUBSCRIPTION,
    {
      refetchQueries: [
        {
          query: graphql.queries.SUBSCRIPTION,
          awaitRefetchQueries: true,
          variables: { where: { id } },
        },
      ],
    },
  );

  const initSubscription = useCallback(
    ({ subscription: data, attachments }) => {
      const subscription = omitDeep(data, "__typename");
      const subscriptionPayment = get(subscription, "payment");
      const paymentsTypes = get(
        subscription,
        "insurancePlan.fields.payment.type",
        [],
      );
      const debitDays = get(
        subscription,
        "insurancePlan.fields.payment.debitDay",
        [],
      );
      const frequencies = get(
        subscription,
        "insurancePlan.fields.payment.frequency",
        [],
      );
      const isAdherent = get(
        subscription,
        "payment.payer.owner.isAdherent",
        true,
      );
      const isTns = get(subscription, "insurancePlan.fields.tns", false);

      if (isEmpty(subscription)) return;
      const contactTelecoms = get(subscription, "project.contact.telecoms", []);
      const payerTelecoms = get(
        subscription,
        "payment.payer.owner.telecoms",
        [],
      );
      const initialDisabled = {
        project: {
          ria: true,
          contract: {
            caseType: get(subscription, "project.ria", false),
            saleMode:
              get(subscription, "project.fields.twoTimesSale", false) ||
              get(subscription, "project.fields.source") ===
                PROJECT_SOURCE.CLIENT,
          },
          contact: {
            siretNumber: !isTns,
            optMadelin: !get(subscription, "insurancePlan.fields.madelin"),
            fields: {
              affiliateOrganizationCode: true,
              requestLafpacMemberShip: !isTns,
              alreadyLafpacMember: !isTns,
              lafpacMemberShipNumber: !get(
                subscription,
                "project.contact.fields.alreadyLafpacMember",
                false,
              ),
            },
            profession: false,
          },
          projectType:
            get(subscription, "project.fields.source") ===
              PROJECT_SOURCE.CLIENT ||
            get(subscription, "project.fields.twoTimesSale") ||
            [SALE_MODES.FACE_TO_FACE].includes(
              get(subscription, "project.contract.saleMode"),
            ),
        },
        createdDate: true,
        proof: {
          payerCin:
            true || get(subscription, "payment.payer.owner.isAdherent", true),
          refundAccount:
            true ||
            get(subscription, "payment.payer.id", "") ===
              get(subscription, "payment.receiver.id"),
          payer: {
            [ATTACHMENT_TYPES.PAYER_CIN]: !(
              get(subscription, "payment.payer.owner.type") ===
              BANK_ACCOUNT_OWNER_TYPE.COMPANY
            ),
            [ATTACHMENT_TYPES.KBIS]: !(
              get(subscription, "payment.payer.owner.type") ===
              BANK_ACCOUNT_OWNER_TYPE.COMPANY
            ),
            [ATTACHMENT_TYPES.PAYER_RIB]: get(
              subscription,
              "payment.payer.owner.isAdherent",
              false,
            ),
          },
        },
        payment: {
          payer: {
            owner: {
              isAdherent: true,
              type: true,
              socialReason:
                isAdherent ||
                get(subscription, "payment.payer.owner.type") !==
                  BANK_ACCOUNT_OWNER_TYPE.COMPANY,
              gender:
                isAdherent ||
                get(subscription, "payment.payer.owner.type") ===
                  BANK_ACCOUNT_OWNER_TYPE.COMPANY,
              firstName:
                isAdherent ||
                get(subscription, "payment.payer.owner.type") ===
                  BANK_ACCOUNT_OWNER_TYPE.COMPANY,
              lastName:
                isAdherent ||
                get(subscription, "payment.payer.owner.type") ===
                  BANK_ACCOUNT_OWNER_TYPE.COMPANY,
            },
          },
          receiver: {
            owner: {
              firstName:
                get(subscription, "payment.payer.id", "") ===
                get(subscription, "payment.receiver.id"),
              lastName:
                get(subscription, "payment.payer.id", "") ===
                get(subscription, "payment.receiver.id"),
            },
            IBAN:
              get(subscription, "payment.payer.id", "") ===
              get(subscription, "payment.receiver.id"),
            BIC:
              get(subscription, "payment.payer.id", "") ===
              get(subscription, "payment.receiver.id"),
          },
        },
        telecoms: {
          payer: isAdherent
            ? {
                [CONTACT_POINT_SYSTEM.STREET]: { value: true },
                [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: true },
                [CONTACT_POINT_SYSTEM.CITY]: { value: true },
              }
            : {
                [CONTACT_POINT_SYSTEM.STREET]: { value: false },
                [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: false },
                [CONTACT_POINT_SYSTEM.CITY]: { value: false },
              },
          contact: {},
        },
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST]:
          !get(subscription, "project.ria", false),
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.BROKERAGE_MANDATE]: !get(
          subscription,
          "project.ria",
          false,
        ),
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER]: !get(
          subscription,
          "project.contact.relatedPersons",
          [],
        ).find((person) => person.relationship === RELATIONSHIP.SPOUSE),
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN]: !get(
          subscription,
          "project.contact.relatedPersons",
          [],
        ).find((person) => person.relationship === RELATIONSHIP.CHILD),
        [SUBSCRIPTION_FORM_KEYS.PROOF.SPOUSE]: !get(
          subscription,
          "project.contact.relatedPersons",
          [],
        ).find((person) => person.relationship === RELATIONSHIP.SPOUSE),
        [SUBSCRIPTION_FORM_KEYS.PROOF.CHILDREN]: !get(
          subscription,
          "project.contact.relatedPersons",
          [],
        ).find((person) => person.relationship === RELATIONSHIP.CHILD),
        [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: isAdherent
          ? true
          : {
              [ATTACHMENT_TYPES.PAYER_CIN]: !(
                get(subscription, "payment.payer.owner.type") ===
                BANK_ACCOUNT_OWNER_TYPE.COMPANY
              ),
              [ATTACHMENT_TYPES.KBIS]: !(
                get(subscription, "payment.payer.owner.type") ===
                BANK_ACCOUNT_OWNER_TYPE.COMPANY
              ),
              [ATTACHMENT_TYPES.PAYER_RIB]: false,
            },
      };

      const telecoms = defaultsDeep(
        {
          contact: keyBy(contactTelecoms, "system"),
          payer: keyBy(isAdherent ? contactTelecoms : payerTelecoms, "system"),
        },
        {
          contact: {
            // [CONTACT_POINT_SYSTEM.ADDRESS_NUMBER]: { value: null },
            // [CONTACT_POINT_SYSTEM.REPETITION_INDEX]: { value: null },
            // [CONTACT_POINT_SYSTEM.STREET]: { value: null },
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
            [CONTACT_POINT_SYSTEM.CITY]: { value: null },
            [CONTACT_POINT_SYSTEM.PHONE]: { value: null },
            [CONTACT_POINT_SYSTEM.LANDLINE]: { value: null },
            [CONTACT_POINT_SYSTEM.EMAIL]: { value: null },
          },
          payer: {
            // [CONTACT_POINT_SYSTEM.ADDRESS_NUMBER]: { value: null },
            // [CONTACT_POINT_SYSTEM.REPETITION_INDEX]: { value: null },
            [CONTACT_POINT_SYSTEM.ADDRESS]: { value: null },
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
            [CONTACT_POINT_SYSTEM.CITY]: { value: null },
          },
        },
      );
      const children = filter(
        get(subscription, "project.contact.relatedPersons", []),
        {
          relationship: RELATIONSHIP.CHILD,
        },
      );
      if (!isEmpty(children))
        children.forEach((child) => {
          set(
            child,
            "identity.fields.teletransmission",
            get(child, "identity.fields.teletransmission", true),
          );
          set(
            child,
            "identity.telecoms",
            keyBy(get(child, "identity.telecoms", []), "system"),
          );
        });

      const spouse = get(
        subscription,
        "project.contact.relatedPersons",
        [],
      ).find(({ relationship }) => relationship === RELATIONSHIP.SPOUSE);
      if (!isEmpty(spouse)) {
        const spouseTelecoms = get(spouse, "identity.telecoms", []);
        set(spouse, "identity.telecoms", keyBy(spouseTelecoms, "system"));
      }
      const familySituation = get(
        subscription,
        "project.contact.fields.familySituation",
      );
      const isRenaultEmployee =
        get(subscription, "project.contact.fields.profile") ===
        PROFILE_LIST.RENAULT_EMPLOYEE;
      const fields = {
        [STEPPER_KEYS.ADDITIONAL_INFORMATION]: {
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTRACT]: [
            "project.contract.issuanceDate",
            "project.contract.caseType",
            "project.ria",
            // "project.contract.operationNature",
            "project.contract.saleMode",
            "project.projectType",
            "createdDate",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST]: [
            "project.terminationRequest.contractNumber",
            "project.terminationRequest.address",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.BROKERAGE_MANDATE]: [
            "project.terminationRequest.brokerageMandate.currentCompany",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADHERENT]: [
            "project.contact.user.lastname",
            "project.contact.user.firstname",
            "project.contact.user.birthDate",
            "project.contact.socialSecurityNumber",
            ...(isRenaultEmployee
              ? []
              : [
                  "project.contact.socialRegime",
                  "project.contact.fields.familySituation",
                ]),
            "project.contact.fields.affiliateOrganizationCode",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.LAFPAC_MEMBERSHIP]:
            isTns
              ? [
                  "project.contact.fields.requestLafpacMemberShip",
                  "project.contact.fields.lafpacMemberShipNumber",
                ]
              : [],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY]: [
            "project.contact.fields.profile",
            "project.contact.socioProfessionalCategory",
          ].concat(
            isTns
              ? [
                  "project.contact.fields.socialReason",
                  "project.contact.siretNumber",
                  "project.contact.fields.nafCode",
                  "project.contact.fields.organizationNumber",
                ]
              : [],
          ),
          // .concat(
          //   isRenaultEmployee ? ["project.contact.fields.matriculation"] : [],
          // ),
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT]: [
            `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER]: [
            "spouse.identity.user.firstname",
            "spouse.identity.user.lastname",
            "spouse.identity.user.birthDate",
            "spouse.identity.socialSecurityNumber",
            "spouse.identity.socialRegime",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN]:
            children.flatMap((_, i) => [
              `children.${i}.identity.user.firstname`,
              `children.${i}.identity.user.lastname`,
              `children.${i}.identity.user.birthDate`,
              `children.${i}.identity.socialRegime`,
              ...(get(_, "identity.fields.refundOtherAccount")
                ? [
                    `children.${i}.identity.fields.payment.IBAN`,
                    `children.${i}.identity.fields.payment.BIC`,
                  ]
                : []),
            ]),
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PAYMENT]: [
            "payment.type",
            "payment.frequency",
            "payment.debitDay",
            "payment.payer.IBAN",
            "payment.payer.BIC",
            "payment.payer.owner.type",
            "payment.payer.owner.socialReason",
            "payment.payer.owner.gender",
            "payment.payer.owner.firstName",
            "payment.payer.owner.lastName",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND]: [
            "isPayerReceiver",
            "payment.receiver.owner.firstName",
            "payment.receiver.owner.lastName",
            "payment.receiver.IBAN",
            "payment.receiver.BIC",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.MARKETING_CONSENTS]:
            [],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION]: [],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.DATA_PROTECTION]: isTns
            ? [
                "project.fields.acceptMutualRegulation",
                "project.fields.acceptMembershipRequestCopy",
                "project.fields.acceptWithdrawalTerms",
                "project.fields.confirmSocialProtectionAffiliation",
                "project.fields.confirmDeclaredActivity",
                "project.fields.confirmSocialContributionsUpToDate",
                "project.fields.commitAnnualContributionProof",
                "project.fields.ackDependentsProofObligation",
                "project.fields.confirmInformationAccuracy",
                // "project.contact.acceptDigital",
              ]
            : [
                "project.fields.acceptProductStatuses",
                "project.fields.acceptFees",
                "project.fields.acceptAgreement",
                // "project.contact.acceptDigital",
              ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION
            .SPONSORSHIP_MEMBERSHIP]: [],
        },
        [STEPPER_KEYS.PROOF]: {
          [SUBSCRIPTION_FORM_KEYS.PROOF.ADHERENT]: [
            `proof.adherent.${ATTACHMENT_TYPES.CIN}`,
            `proof.adherent.${ATTACHMENT_TYPES.PAYMENT_ACCOUNT}`,
            `proof.adherent.${ATTACHMENT_TYPES.SOCIAL_SECURITY_CERTIFICATE}`,
            ...(get(subscription, "project.ria")
              ? [
                  `proof.adherent.${ATTACHMENT_TYPES.ELECTRONIC_SIGNATURE_ELIGIBILITY}`,
                ]
              : []),
          ],
          [SUBSCRIPTION_FORM_KEYS.PROOF.SPOUSE]: [
            `proof.spouse.${ATTACHMENT_TYPES.SOCIAL_SECURITY_CERTIFICATE}`,
            ...(familySituation && familySituation !== FAMILY_SITUATION.MARRIED
              ? [`proof.spouse.${ATTACHMENT_TYPES.MARITAL_CERTIFICATE}`]
              : []),
          ],
          [SUBSCRIPTION_FORM_KEYS.PROOF.CHILDREN]: children
            .flatMap((child, i) => {
              const age = moment().diff(
                get(child, "identity.user.birthDate"),
                "years",
              );
              const childId = get(child, "identity.user.id");
              const attachmentType =
                age < 18
                  ? ATTACHMENT_TYPES.SOCIAL_SECURITY_CERTIFICATE
                  : ATTACHMENT_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE;
              return [
                `proof.children.${childId}.${attachmentType}`,
                get(child, "identity.fields.refundOtherAccount")
                  ? `proof.children.${childId}.${ATTACHMENT_TYPES.PAYMENT_ACCOUNT}`
                  : undefined,
              ];
            })
            .filter(Boolean),
          [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: [
            `proof.payer.${ATTACHMENT_TYPES.PAYER_CIN}`,
            `proof.payer.${ATTACHMENT_TYPES.KBIS}`,
            `proof.payer.${ATTACHMENT_TYPES.PAYER_RIB}`,
          ],
        },
      };

      const additionalInfoInitialValues = {
        ...subscription,
        telecoms,
        project: {
          ...subscription.project,
          ...(subscription.project.fields.twoTimesSale && {
            projectType: PROJECT_TYPES.COLD_LEAD,
          }),
          contact: defaultsDeep(subscription.project.contact, {
            fields: { teletransmission: true },
            acceptContact: true,
          }),
          contract: {
            ...subscription.project.contract,
            caseType: subscription.project.ria
              ? CASE_TYPES.RETURN_TO_COMPETITION
              : get(subscription, "project.contract.caseType") ||
                CASE_TYPES.NEW_CONTRACT,
            ...(subscription.project.fields.twoTimesSale && {
              saleMode: SALE_MODES.DISTANT,
            }),
          },
          ...(subscription.project.ria
            ? {
                terminationRequest: {
                  id: cuid(),
                  ...subscription.project.terminationRequest,
                  brokerageMandate: {
                    id: cuid(),
                    firstName: get(
                      subscription,
                      "project.contact.user.firstname",
                    ),
                    lastName: get(
                      subscription,
                      "project.contact.user.lastname",
                    ),
                    ...subscription.project.terminationRequest
                      ?.brokerageMandate,
                  },
                },
              }
            : {}),
        },
        isPayerReceiver:
          get(subscription, "payment.payer.id", "") ===
          get(subscription, "payment.receiver.id")
            ? PAYMENT_RECEIVER.SAME_ACCOUNT
            : PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
        payment: {
          id: cuid(),
          ...subscriptionPayment,
          type: get(
            subscriptionPayment,
            "type",
            paymentsTypes.length === 1 ? head(paymentsTypes) : null,
          ),
          debitDay: get(
            subscriptionPayment,
            "debitDay",
            debitDays.length === 1 ? head(debitDays) : "5",
          ),
          frequency: get(
            subscriptionPayment,
            "frequency",
            frequencies.length === 1 ? head(frequencies) : null,
          ),
          payer: {
            id: cuid(),
            IBAN: null,
            BIC: null,
            ...subscription.payment?.payer,
            owner: {
              id: cuid(),
              ...subscription.payment?.payer?.owner,
              firstName: defaultTo(
                subscription.payment?.payer?.owner?.firstName,
                isAdherent
                  ? get(subscription, "project.contact.user.firstname")
                  : null,
              ),
              lastName: defaultTo(
                subscription.payment?.payer?.owner?.lastName,
                isAdherent
                  ? get(subscription, "project.contact.user.lastname")
                  : null,
              ),
              isAdherent: defaultTo(
                subscription.payment?.payer?.owner?.isAdherent,
                true,
              ),
            },
          },
          receiver: defaultsDeep(subscription.payment?.receiver, {
            id: cuid(),
            IBAN: null,
            BIC: null,
            owner: { id: cuid() },
          }),
        },
        children,
        spouse: isEmpty(spouse)
          ? undefined
          : defaultsDeep(spouse, {
              identity: { fields: { teletransmission: true } },
            }),
      };

      const adherentId = get(subscription, "project.contact.user.id");
      const spouseId = get(spouse, "identity.user.id");
      const groupAttachmentsByType = (userId, allowedTypes) =>
        mapValues(
          groupBy(
            filter(
              attachments,
              ({ user, type }) =>
                user?.id === userId && allowedTypes.includes(type),
            ).map(({ id, name, fileUrl, type }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
              type,
            })),
            "type",
          ),
          (arr) => arr.map(({ type, ...rest }) => rest),
        );

      const childrenProof = children.reduce((acc, child) => {
        const childId = get(child, "identity.user.id");
        if (childId) {
          acc[childId] = groupAttachmentsByType(childId, CHILDREN_DOCUMENTS);
        }
        return acc;
      }, {});

      const proofInitialValues = {
        proof: {
          adherent: groupAttachmentsByType(adherentId, ADHERENT_DOCUMENTS),
          payer: groupAttachmentsByType(adherentId, PAYER_DOCUMENTS),
          spouse: groupAttachmentsByType(spouseId, SPOUSE_DOCUMENTS),
          children: childrenProof,
        },
      };

      setDisabled(initialDisabled);
      setInitialValues({
        [STEPPER_KEYS.ADDITIONAL_INFORMATION]: additionalInfoInitialValues,
        [STEPPER_KEYS.PROOF]: proofInitialValues,
        fields,
      });

      if (subscription.status === SUBSCRIPTION_STATUSES.SIGNED)
        setActive(STEPPER_KEYS.DOCUMENT);
      if (subscription.status === SUBSCRIPTION_STATUSES.PENDING)
        setActive(STEPPER_KEYS.DOCUMENT);
    },
    [],
  );

  const { data, loading, error } = useQuery(graphql.queries.SUBSCRIPTION, {
    variables: { where: { id } },
    onCompleted: ({ subscription }) => {
      getAttachments({
        onCompleted: ({ attachments: { data } }) =>
          initSubscription({ subscription, attachments: data }),
      });
    },
  });

  const [getAttachments] = useLazyQuery(graphql.queries.ATTACHMENTS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        AND: [{ subscription: { id: get(data, "subscription.id") } }],
      },
      isIn: {
        type: [
          ...ADHERENT_DOCUMENTS,
          ...PAYER_DOCUMENTS,
          ...SPOUSE_DOCUMENTS,
          ...CHILDREN_DOCUMENTS,
        ],
      },
    },
  });

  const [active, setActive] = useState(STEPPER_KEYS.ADDITIONAL_INFORMATION);
  const [disabled, setDisabled] = useState({});
  const [realProgress, setRealProgress] = useState({
    [STEPPER_KEYS.ADDITIONAL_INFORMATION]: 0,
    [STEPPER_KEYS.PROOF]: 0,
  });
  const [progress, setProgress] = useState({
    [STEPPER_KEYS.ADDITIONAL_INFORMATION]: {},
    [STEPPER_KEYS.PROOF]: {},
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [active]);

  const calculateBlockProgress = (parent, block, values, disabled) => {
    if (disabled[block] === true) return;

    let fieldList = Object.values(
      get(initialValues, `fields.${parent}.${block}`, []),
    );

    if (parent === STEPPER_KEYS.ADDITIONAL_INFORMATION) {
      const registeredPaths = new Set(fieldStore.getRegisteredPaths());
      if (registeredPaths.size > 0) {
        fieldList = fieldList.filter((path) => registeredPaths.has(path));
      }
    }

    const maxLength = fieldList.filter((g) => g && !get(disabled, g)).length;

    const filedLength = fieldList.filter((g) => {
      const value = get(values, g);
      const isDisabled = get(disabled, g);

      if (isDisabled) return false;
      if (isNil(value)) return false;
      if (value === "") return false;

      if (
        block ===
        SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.MARKETING_CONSENTS
      ) {
        if (
          g === "project.contact.fields.acceptEmailOffers" ||
          g === "project.contact.fields.acceptSMSOffers"
        ) {
          return value === true;
        }
      }
      if (block === SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION) {
        if (g === "project.fields.acceptProductDocuments") {
          return value === true;
        }
      }

      if (
        block === SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.DATA_PROTECTION
      ) {
        if (
          g === "project.fields.acceptProductStatuses" ||
          g === "project.fields.acceptFees" ||
          g === "project.fields.acceptAgreement" ||
          g === "project.fields.acceptMutualRegulation" ||
          g === "project.fields.acceptMembershipRequestCopy" ||
          g === "project.fields.acceptWithdrawalTerms" ||
          g === "project.fields.confirmSocialProtectionAffiliation" ||
          g === "project.fields.confirmDeclaredActivity" ||
          g === "project.fields.confirmSocialContributionsUpToDate" ||
          g === "project.fields.commitAnnualContributionProof" ||
          g === "project.fields.ackDependentsProofObligation" ||
          g === "project.fields.confirmInformationAccuracy" ||
          g === "project.contact.acceptDigital"
        ) {
          return value === true;
        }
      }

      if (isObject(value) && isEmpty(value)) return false;

      return true;
    }).length;

    return maxLength ? Math.ceil((filedLength / maxLength) * 100) : 100;
  };

  const calculateRealTimeProgress = (
    parent,
    block,
    values,
    initialDisabled = disabled,
  ) => {
    setProgress((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [block]: calculateBlockProgress(parent, block, values, initialDisabled),
      },
    }));
  };

  const calculateProgressMean = (
    parent,
    values,
    initialDisabled = disabled,
  ) => {
    const { count, sum } = Object.keys(
      get(initialValues, `fields.${parent}`, []),
    ).reduce(
      (acc, block) => {
        const progress = calculateBlockProgress(
          parent,
          block,
          values,
          initialDisabled,
        );

        if (block === SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE)
          return acc;
        return isNumber(progress)
          ? { sum: (acc.sum += progress), count: (acc.count += 1) }
          : acc;
      },
      { count: 0, sum: 0 },
    );
    const progress = +(sum / count).toFixed(2);

    setRealProgress((prev) => ({
      ...prev,
      [parent]: progress,
    }));

    if (
      parent === STEPPER_KEYS.PROOF &&
      ((progress === 100 && realProgress[parent] !== 100) ||
        (progress !== 100 && realProgress[parent] === 100))
    ) {
      return updateSubscription({
        variables: {
          where: { id },
          data: {
            additionalInfo: {
              ...get(
                omitDeep(data, "__typename"),
                "subscription.additionalInfo",
                {},
              ),
              isDocumentsCompleted: progress === 100,
            },
          },
        },
        onCompleted: () => setIsSubmitting(false),
      });
    }
    if (
      parent === STEPPER_KEYS.ADDITIONAL_INFORMATION &&
      ((progress === 100 && realProgress[parent] !== 100) ||
        (progress !== 100 && realProgress[parent] === 100))
    ) {
      return updateSubscription({
        variables: {
          where: { id },
          data: {
            additionalInfo: {
              ...get(
                omitDeep(data, "__typename"),
                "subscription.additionalInfo",
                {},
              ),
              ...get(values, "additionalInfo", {}),
              isInfoCompleted: progress === 100,
            },
          },
        },
        onCompleted: () => setIsSubmitting(false),
      });
    }
    setIsSubmitting(false);
  };

  if (loading || error || isEmpty(initialValues) || isEmpty(disabled))
    return <Loading />;

  return (
    <FieldRegistryProvider store={fieldStore}>
      <View
        active={active}
        setActive={setActive}
        data={omitDeep(data, "__typename")}
        progress={progress}
        setProgress={setProgress}
        disabled={disabled}
        setDisabled={setDisabled}
        setPercent={calculateRealTimeProgress}
        realProgress={realProgress}
        calculateProgressMean={calculateProgressMean}
        initialValues={initialValues}
        checkUser={checkUser}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </FieldRegistryProvider>
  );
};

export default Detail;
